import { Drawer, Button, SVGWrapper, Spacer } from '../../../../components'
import { IconClose } from '../../../../assets/icons'
import { Page } from '../../elements'
import LogoGcSmall from '../../../../assets/images/LogoGcSmall.png'
import Logo3NSmall from '../../../../assets/images/Logo3NSmall.png'
import Logo3nUni from '../../../../assets/images/Logo3nUni.png'
import {
  ArtPieChart,
  ArtPlant,
  ArtRetirementSmall,
  ArtStudyMaterials,
  BulletInvestment,
  BulletLoans,
  BulletPiggyBank,
  BulletStatistics,
  Bullet3nUniCoach,
  Bullet3nUniLessons,
  Bullet3nUniQA,
  BulletCalculator,
  BulletChecklist,
  BulletBook,
} from '../../../../assets/icons'
import { css } from '@emotion/react'
import { cloneElement, useEffect, useState } from 'react'
import {
  formatDollars,
  internalFreeTrialTerms,
  intervalAbbrev,
  isFreeTrial30Day,
  isInternalFreeTrial,
  priceSubtext,
  showsAsterisk,
  showsInterval,
} from '../pricebook-display'

const LearnMoreDrawer = ({ drawerOpen, setDrawerOpen, pricebookBundle }) => {
  return (
    <Drawer active={drawerOpen} width='700px' fullMobile>
      {/* Mobile: an X in the top-right corner. */}
      <button
        type='button'
        aria-label='Close'
        onClick={() => setDrawerOpen(false)}
        css={css`
          position: absolute;
          top: 20px;
          right: 20px;
          z-index: 1;
          border: none;
          background: none;
          padding: 0;
          cursor: pointer;
          line-height: 0;
          @media (min-width: 801px) {
            display: none;
          }
        `}>
        <SVGWrapper svg={IconClose} size='large' margin='0px' />
      </button>
      <LearnMoreContent pricebookBundle={pricebookBundle} />
      {/* Desktop: a secondary "Close" button at the bottom-right. */}
      <div
        css={css`
          display: flex;
          justify-content: flex-start;
          margin-top: 20px;
          @media (max-width: 800px) {
            display: none;
          }
        `}>
        <Button variant='secondary' size='small' onClick={() => setDrawerOpen(false)}>
          Close
        </Button>
      </div>
    </Drawer>
  )
}

const LearnMoreContent = ({ pricebookBundle }) => {
  if (!pricebookBundle) return null

  return (
    <>
      <Header pricebookBundle={pricebookBundle} />
      <BundleCard pricebookBundle={pricebookBundle} />
      <Description pricebookBundle={pricebookBundle} />
      <Bullets pricebookBundle={pricebookBundle} />
      <ExtraExplainer pricebookBundle={pricebookBundle} />
    </>
  )
}

// Small gray eyebrow + slate product name. On desktop, the GC / 3Nickels logo
// sits in the top-right; on mobile there is no logo (per the mobile design).
const Header = ({ pricebookBundle }) => {
  const { productType, pricebookName } = pricebookBundle?.pricebook ?? undefined
  const logo =
    productType === 'GUIDED_CHOICE'
      ? LogoGcSmall
      : productType === 'NICKELSU'
        ? Logo3nUni
        : Logo3NSmall

  return (
    <Row style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Column style={{ paddingRight: '32px' }}>
        <Page.WhoPaysText>
          {productType === 'GUIDED_CHOICE' ? 'GuidedChoice packages' : '3Nickels packages'}
        </Page.WhoPaysText>
        <Spacer space='15px' />
        <Page.PricebookNameText>{pricebookName}</Page.PricebookNameText>
      </Column>
      <img
        src={logo}
        alt='Product logo'
        css={css`
          height: 40px;
          width: auto;
          flex-shrink: 0;
          @media (max-width: 800px) {
            display: none;
          }
        `}
      />
    </Row>
  )
}

// Vertical stack: product art (centered) → bulleted description → price inner-card.
const BundleCard = ({ pricebookBundle }) => {
  const pricebook = pricebookBundle.prices[0]
  const orgPays = pricebookBundle.pricebook.paidBy === 'ORG'
  const promotion = pricebookBundle.prices.find(price => price.introductionPeriodInDays > 1)
  const introPeriodInMonths = Math.floor((promotion?.introductionPeriodInDays ?? 0) / 30)
  const freeTrial30Day = isFreeTrial30Day(pricebookBundle)
  const internalFreeTrial = isInternalFreeTrial(pricebookBundle)
  const price = pricebook.price / 100.0
  const digits = formatDollars(price).replace('$', '')

  return (
    <>
      <Spacer space='10px' />
      <Page.BundleCard>
        <img
          src={pricebookBundle.pricebook.artUrl}
          alt='Product Art'
          css={css`
            height: 70px;
            flex-shrink: 0;
          `}
        />
        {/* Mobile-only vertical gaps; on desktop the row layout provides spacing. */}
        <div
          css={css`
            height: 30px;
            @media (min-width: 801px) {
              display: none;
            }
          `}
        />
        <div
          css={css`
            width: 100%;
            & > div {
              font-size: 16px;
            }
            @media (min-width: 801px) {
              flex: 1;
              & > div {
                font-size: 14px;
              }
            }
          `}>
          {pricebookBundle.pricebook.description.map((bullet, index) => (
            <Row key={index} style={{ marginBottom: '4px' }}>
              <div style={{ marginRight: '12px' }}>•</div>
              <div>{bullet}</div>
            </Row>
          ))}
        </div>
        <div
          css={css`
            height: 20px;
            @media (min-width: 801px) {
              display: none;
            }
          `}
        />
        <Page.InnerCard
          css={css`
            @media (min-width: 801px) {
              width: 196px;
              flex-shrink: 0;
            }
          `}>
          {orgPays ? (
            <Page.OrgPaysText>
              paid by your <br /> organization
            </Page.OrgPaysText>
          ) : (
            <>
              <Page.PricebookSubHeading>
                {freeTrial30Day ? (
                  '30 days free'
                ) : (
                  <>
                    <span className='currency'>$</span>
                    <span className='amount'>{digits}</span>
                    {showsInterval(pricebookBundle) && (
                      <span className='interval'>/{intervalAbbrev[pricebook.intervalType]}</span>
                    )}
                    {showsAsterisk(pricebookBundle) && <span className='asterisk'>*</span>}
                  </>
                )}
              </Page.PricebookSubHeading>
              <Spacer space='5px' />
              <div
                css={css`
                  align-self: center;
                  font-size: 12px;
                  font-weight: 600;
                `}>
                {priceSubtext(pricebookBundle)}
              </div>
            </>
          )}
        </Page.InnerCard>
      </Page.BundleCard>
      {promotion &&
        (introPeriodInMonths === 1 ? (
          <div>* After 30 days, Advice subscription auto-renews at $14.99/month</div>
        ) : (
          <div>
            * After {introPeriodInMonths} months, Advice subscription auto-renews at $14.99/month
          </div>
        ))}
      {/* The 32-day trial's price reads "$0*"; its terms live here. */}
      {internalFreeTrial && <div>* {internalFreeTrialTerms(pricebookBundle)}</div>}
    </>
  )
}

// Desktop: heading + body with the art to its right. Mobile: art centered above the text.
const Description = ({ pricebookBundle }) => {
  const [state, setState] = useState(undefined)
  const productType = pricebookBundle.pricebook.productType
  const pricebookName = pricebookBundle.pricebook.pricebookName

  useEffect(() => {
    if (!productType) return

    if (productType === 'GUIDED_CHOICE') {
      setState({
        art: ArtRetirementSmall,
        descriptionText: `One of the questions we’re asked the most is how to create a safe after-retirement income plan. That’s why we’ve put our expertise to work on personalized planning everyone near retirement or already retired can afford, and use to make confident decisions.`,
      })
    } else if (productType === 'NICKELSU') {
      setState({
        art: ArtStudyMaterials,
        descriptionText: `No matter where you're at on your journey, we're all about setting you up for success. 3NickelsU is a program that teaches you exactly how to get on the path to financial freedom. Just a few hours can set you up for life!`,
      })
    } else if (productType === 'NICKELS') {
      if (pricebookName === '3Nickels Advice') {
        setState({
          art: ArtPieChart,
          descriptionText: `Experience The Financial Advisor In Your Pocket®!`,
          extraDescription: `Connect all of your finances in one place and let us do the heavy lifting. We've been advising people on their finances for over 20 years, backed by our sophisticated, award-winning technology. Now it's in your hands!`,
        })
      } else {
        setState({
          art: ArtPlant,
          descriptionText: `Get answers to your basic financial questions at no cost to you. Work through different aspects of your finances, one at a time.`,
        })
      }
    }
  }, [pricebookName, productType])

  if (!state) return null
  return (
    <>
      <Spacer space='15px' />
      {/* Desktop: text on the left, art on the right (row).
          Mobile: art centered above the text (art reordered to the top). */}
      <div
        css={css`
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          @media (max-width: 800px) {
            flex-direction: column;
            align-items: stretch;
            gap: 0;
          }
        `}>
        <div
          css={css`
            flex: 1;
          `}>
          <Page.DescriptionTitleText>Description</Page.DescriptionTitleText>
          <Page.DescriptionText>{state.descriptionText}</Page.DescriptionText>
          {state.extraDescription && (
            <>
              <Spacer space='8px' />
              <Page.DescriptionText>{state.extraDescription}</Page.DescriptionText>
            </>
          )}
        </div>
        <div
          css={css`
            flex-shrink: 0;
            @media (max-width: 800px) {
              order: -1;
              display: flex;
              justify-content: center;
              margin-bottom: 10px;
            }
          `}>
          <SVGWrapper svg={state.art} />
        </div>
      </div>
    </>
  )
}

const Bullets = ({ pricebookBundle }) => {
  const [bullets, setBullets] = useState(undefined)
  const productType = pricebookBundle.pricebook.productType
  const pricebookName = pricebookBundle.pricebook.pricebookName

  useEffect(() => {
    if (!productType) return

    if (productType === 'GUIDED_CHOICE') {
      setBullets([
        <BulletLine
          art={BulletPiggyBank}
          text={
            <div>
              Know exactly how much to <strong>save</strong>, across your accounts, to reach your
              retirement goals
            </div>
          }
        />,
        <BulletLine
          art={BulletInvestment}
          text={
            <div>
              Know exactly what to <strong>invest</strong> in and at what percent
            </div>
          }
        />,
        <BulletLine
          art={BulletStatistics}
          text={
            <div>
              <strong>Retirement income projections</strong> for a weak, average, and strong market
            </div>
          }
        />,
        <BulletLine
          art={BulletLoans}
          text={
            <div>
              <strong>Drawdown recommendations</strong> after retirement
            </div>
          }
        />,
      ])
    } else if (productType === 'NICKELSU') {
      setBullets([
        <BulletLine
          art={Bullet3nUniCoach}
          text={
            <div>
              Personalized one-on-one <strong>coaching</strong>
            </div>
          }
        />,
        <BulletLine
          art={Bullet3nUniLessons}
          text={
            <div>
              30 quick <strong>lessons</strong>
            </div>
          }
        />,
        <BulletLine
          art={Bullet3nUniQA}
          text={
            <div>
              Live <strong>Q&amp;A</strong>
            </div>
          }
        />,
      ])
    } else if (productType === 'NICKELS') {
      if (pricebookName === '3Nickels Advice') {
        setBullets([
          <BulletLine
            art={BulletBook}
            text={
              <div>
                <strong>Accessible education</strong> at every level of financial literacy.
              </div>
            }
          />,
          <BulletLine
            art={BulletCalculator}
            text={
              <div>
                <strong>Customizable tools</strong> to help you with budgeting, retirement, debt
                payoff and much more.
              </div>
            }
          />,
          <BulletLine
            art={BulletChecklist}
            text={
              <div>
                <strong>Actionable financial planning</strong> to help you reach your goals.
              </div>
            }
          />,
          <BulletLine
            art={BulletInvestment}
            text={
              <div>
                <strong>Objective investment advice</strong> for each of your personal accounts.
              </div>
            }
          />,
        ])
      } else {
        setBullets([
          <BulletLine
            art={BulletBook}
            text={
              <div>
                <strong>Accessible education</strong> at an introductory level.
              </div>
            }
          />,
          <BulletLine
            art={BulletCalculator}
            text={
              <div>
                <strong>Calculators and checklists</strong> to help you answer one-off questions.
              </div>
            }
          />,
        ])
      }
    }
  }, [pricebookName, productType])

  if (!bullets) return null
  return (
    <>
      <Spacer space='10px' />
      <Page.DescriptionTitleText>At a glance…</Page.DescriptionTitleText>
      <Spacer space='5px' />
      {bullets.map((bullet, index) => cloneElement(bullet, { key: index }))}
    </>
  )
}

const BulletLine = ({ art, text }) => {
  return (
    <Row style={{ alignItems: 'center', marginBottom: '20px' }}>
      <SVGWrapper svg={art} />
      <Spacer space='10px' />
      <Page.PackageBulletText>{text}</Page.PackageBulletText>
    </Row>
  )
}

const ExtraExplainer = ({ pricebookBundle }) => {
  const productType = pricebookBundle.pricebook.productType
  const subscriptionMonths = pricebookBundle.pricebook.metadata?.subscriptionMonths ?? 'two'
  const eduPricebook = pricebookBundle.pricebook.description.some(item => item.includes('.edu'))
  const accessToAdviceText = eduPricebook
    ? 'This promotion offers you access to the Advice tier of 3Nickels.'
    : `For ${subscriptionMonths} month${subscriptionMonths === 1 ? '' : 's'}, you have access to
the Advice tier of 3Nickels.`

  if (productType === 'GUIDED_CHOICE' || productType === 'NICKELS') return null

  return (
    <>
      <Spacer space='7px' />
      <Page.DescriptionTitleText>And you have access to 3Nickels Advice!</Page.DescriptionTitleText>
      <Spacer space='5px' />
      <Row>
        <SVGWrapper svg={ArtPieChart} />
        <Column>
          <Page.DescriptionText>
            {accessToAdviceText} Experience The Financial Advisor In Your Pocket&#0174;!
          </Page.DescriptionText>
          <Spacer space='5px' />
          <Page.DescriptionText>
            Connect all of your finances in one place and let us do the heavy lifting.
          </Page.DescriptionText>
        </Column>
      </Row>
      <Spacer space='10px' />
    </>
  )
}

const Row = ({ style, children }) => {
  return <div style={{ display: 'flex', flexDirection: 'row', ...style }}>{children}</div>
}

const Column = ({ style, children }) => {
  return <div style={{ display: 'flex', flexDirection: 'column', ...style }}>{children}</div>
}

export default LearnMoreDrawer
